/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import * as React from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import * as SelectPrimitive from '@radix-ui/react-select';

import { Button } from '@/components/ui/button';
import { SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

export interface EnhancedSelectOption {
  value: string;
  label: string;
}

interface EnhancedSelectProps extends Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>,
  'children'
> {
  options: EnhancedSelectOption[];
  placeholder?: string;
  onClear?: () => void;
  clearLabel?: string;
  id?: string;
}

const EnhancedSelect = ({
  options,
  placeholder,
  onClear,
  clearLabel,
  onValueChange,
  open: openProp,
  onOpenChange,
  id,
  value,
  ...props
}: EnhancedSelectProps) => {
  const [openState, setOpenState] = React.useState(false);
  const [resetKey, setResetKey] = React.useState(0);
  const [internalValue, setInternalValue] = React.useState(value ?? '');
  const { messages } = useLanguage();

  const hasValue = value !== undefined ? !!value : !!internalValue;

  // When the external value is cleared (e.g. parent resets it), bump resetKey
  // so Radix Select re-mounts and shows the placeholder instead of the stale value.
  React.useEffect(() => {
    if (!value) {
      setResetKey((k) => k + 1);
      setInternalValue('');
    } else {
      setInternalValue(value);
    }
  }, [value]);

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openState;

  const handleOpenChange = (value: boolean) => {
    if (!isControlled) setOpenState(value);
    onOpenChange?.(value);
  };

  const handleClear = () => {
    setResetKey((k) => k + 1);
    setInternalValue('');
    onClear?.();
    handleOpenChange(false);
  };

  const label =
    clearLabel ?? messages?.PromptFilters?.multi_select?.clear ?? 'Clear';

  return (
    <SelectPrimitive.Root
      key={resetKey}
      open={open}
      onOpenChange={handleOpenChange}
      onValueChange={(v) => {
        setInternalValue(v);
        onValueChange?.(v);
      }}
      value={value ?? ''}
      {...props}
    >
      <SelectTrigger
        id={id}
        className="h-auto w-full gap-1.5 border-none p-0 text-[13px] leading-[18px] text-body-subtle focus:ring-0 focus:ring-offset-0 [&>svg]:text-[#EBEFF1] [&>svg]:opacity-100"
      >
        <div className="min-w-0 flex-1 truncate text-left">
          <SelectValue placeholder={placeholder} />
        </div>
      </SelectTrigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="relative z-50 min-w-[8rem] overflow-visible rounded-none border bg-white text-[#201F1E] shadow-md data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          position="popper"
          sideOffset={8}
        >
          <SelectPrimitive.Viewport className="max-h-60 w-full min-w-[var(--radix-select-trigger-width)] overflow-y-auto p-1">
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="cursor-pointer focus:bg-[#EDEBE9] focus:text-[#201F1E]"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectPrimitive.Viewport>
          {onClear && hasValue && (
            <div className="rounded-b-sm bg-white">
              <Separator className="bg-[#C1C9CF]" />
              <div className="flex justify-center text-btn-label text-[#0A2D3A]">
                <Button
                  variant="ghost"
                  onClick={handleClear}
                  className=" my-1 h-7 w-full cursor-pointer justify-center hover:bg-[#EDEBE9] hover:text-[#0A2D3A]"
                >
                  {label}
                </Button>
              </div>
            </div>
          )}
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

EnhancedSelect.displayName = 'EnhancedSelect';

export { EnhancedSelect };
